/**
 * Room store —— 当前房间的 room_state 容器。
 *
 * 进程内只存"我所在的那个房间", 离开 / 切换时整体 reset。
 *
 * 数据流:
 * - WS 推 `room_state` → applyRoomState() 整体替换;
 * - WS 推 `event`      → recordEvent() 只做 dev console + 最近一条记录;
 *   动画驱动留到 M7, 此处不消费 event。
 *
 * 派生 computed:
 * - currentPlayer(actor_pid): 谁在行动
 * - seatsArray: [{seat, slot}] 按 seat 升序的稳定数组
 * - me(): 我的 PublicPlayer (若已坐下)
 * - opponents(): 其他在座玩家
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  PublicPlayer,
  RoomEventFrame,
  RoomStateFrame,
  SeatSlot,
} from '@/ws/frames'

export interface SeatEntry {
  seat: number
  slot: SeatSlot | null
}

export const useRoomStore = defineStore('room', () => {
  /** 当前 room_state 快照, 离开房间时归 null。*/
  const state = ref<RoomStateFrame | null>(null)
  /** 最近一条 event (主要用于调试 / 动画 hint), M7 再正式消费。*/
  const lastEvent = ref<RoomEventFrame | null>(null)
  const lastHandRecorded = ref<{ hand_id: string; seq: number } | null>(null)

  // ── 派生 ────────────────────────────────────────────────────

  const room  = computed(() => state.value?.room  ?? null)
  const phase = computed(() => state.value?.phase ?? null)
  const game  = computed(() => state.value?.game  ?? null)
  const you   = computed(() => state.value?.you   ?? null)
  const allHoles = computed(() => state.value?.all_holes ?? {})

  /** 座位列表, 按 seat 索引升序。空位 slot=null。max_seats 决定长度。*/
  const seats = computed<SeatEntry[]>(() => {
    if (!room.value) return []
    const out: SeatEntry[] = []
    for (let i = 0; i < room.value.max_seats; i++) {
      out.push({ seat: i, slot: room.value.seats[String(i)] ?? null })
    }
    return out
  })

  /** pid -> PublicPlayer 索引, 用于按 pid 查 in-stack 玩家。*/
  const playersByPid = computed<Map<string, PublicPlayer>>(() => {
    const m = new Map<string, PublicPlayer>()
    for (const p of game.value?.players ?? []) m.set(p.pid, p)
    return m
  })

  /** 我自己作为 PublicPlayer (仅在我已坐下且在牌局视野时存在)。*/
  const meAsPlayer = computed<PublicPlayer | null>(() => {
    if (!you.value) return null
    return playersByPid.value.get(you.value.pid) ?? null
  })

  /** 当前行动者 pid (BETTING 阶段才有)。*/
  const actorPid = computed<string | null>(() => phase.value?.actor_pid ?? null)
  const isMyTurn = computed(() => !!(actorPid.value && you.value && actorPid.value === you.value.pid))

  // ── 写入 ────────────────────────────────────────────────────

  function applyRoomState(frame: RoomStateFrame): void {
    // hand_seq 倒退一般意味着切到另一个房间或重连场景;
    // 一律以最新 frame 为准, 不做版本号过滤 (后端是单一真相)。
    state.value = frame
  }

  function recordEvent(frame: RoomEventFrame): void {
    lastEvent.value = frame
    if (frame.kind === 'hand_recorded') {
      lastHandRecorded.value = frame.data as { hand_id: string; seq: number }
    }
    // 留接口给 M7 的动画 bus: useAnimations().push(frame.kind, frame.data)
  }

  function reset(): void {
    state.value = null
    lastEvent.value = null
    lastHandRecorded.value = null
  }

  return {
    // raw
    state, lastEvent, lastHandRecorded,
    // derived
    room, phase, game, you, allHoles,
    seats, playersByPid, meAsPlayer, actorPid, isMyTurn,
    // mutations
    applyRoomState, recordEvent, reset,
  }
})
